import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import moment from 'moment';
import { NgIf, NgFor } from '@angular/common';

import { ColDefWithFields } from '../column-type';
import { getFormatDate, getParseDate } from '../../utils/resolved-format-date';
import { FilterDateFormatType } from '../../models/filter-formats';
import { NumberFormat, formatNumber } from '../../utils/number.format';

import { MultiLineValueTypes } from './multi-line-value-types';

function resolveCellValue<TData, TValue>(
	cellValue?: string | number | null,
	currentColDef?: ColDefWithFields<TData, TValue>
) {
	if (cellValue === undefined || cellValue === null || currentColDef?.valueType === undefined) {
		return undefined;
	}

	const normalizedCellValue = String(cellValue);
	if (!normalizedCellValue) {
		return undefined;
	}

	const valueArray = normalizedCellValue.split(';');
	if (currentColDef.valueType === MultiLineValueTypes.NUMBER) {
		const numberArray = [];
		const { numericFormat } = <{ numericFormat?: NumberFormat } | undefined> currentColDef.cellRendererParams || {};
		if (!numericFormat) {
			for (const item of valueArray) {
				numberArray.push(item === '' ? '' : Number.parseFloat(item).toString());
			}
		} else {
			for (const item of valueArray) {
				numberArray.push(item === '' ? '' : formatNumber(Number.parseFloat(item), numericFormat));
			}
		}
		return numberArray;
	} else if (currentColDef.valueType === MultiLineValueTypes.DATE) {
		const dateArray = [];
		for (const item of valueArray) {
			dateArray.push(item === ''
				? ''
				: moment(
					item,
					getParseDate(
						(<FilterDateFormatType>currentColDef.cellRendererParams).inputFormat
					)
				)
					.format(getFormatDate((<FilterDateFormatType>currentColDef.cellRendererParams).outputFormat)));
		}
		return dateArray;
	}
	return valueArray;
}

@Component({
	selector: 'copo-multi-line-value-cell',
	templateUrl: './multi-line-value-cell.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgIf, NgFor]
})
export class MultiLineValueCellComponent implements ICellRendererAngularComp {
	@HostBinding('class.passthrough') readonly passthrough = true;

	params?: ICellRendererParams<unknown, unknown>;
	values: string[] | undefined = [];
	valueType: MultiLineValueTypes | undefined;

	constructor(
		readonly changeDetectorRef: ChangeDetectorRef,
	) {}

	agInit(params: ICellRendererParams<unknown, unknown>) {
		this.params = params;
		const currentColDef = <ColDefWithFields> params.colDef;
		this.valueType = currentColDef.valueType;
		this.values = resolveCellValue(
			<string | number | null | undefined> this.params.value,
			currentColDef
		);
		this.changeDetectorRef.markForCheck();
	}

	refresh(params: ICellRendererParams<unknown, unknown>) {
		this.params = params;
		this.changeDetectorRef.markForCheck();
		return true;
	}
}
